package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.HistoryEntryDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.TranslationOrigin
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDiffParts
import dev.lingvo.site.ds.LDiffText
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorActivityContent(state: TranslationEditorState, row: KeyRow) {
    val currentUserId = Session.user?.id
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        when (val history = state.panels.history[row.key.id] ?: Async.Loading) {
            Async.Loading -> {
                // One region for the four bars. Each `LSkeleton` defaults to its own `role="status"`
                // saying "Loading content", and a `repeat` renders that call site four times — so
                // the panel announced the same sentence four times over. A single call site is not
                // a single announcement.
                Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
                    Text(t("editor.editor_activity_content.loading_the_history", "Loading the history"))
                }
                repeat(4) { LSkeleton(height = 90.px, label = null) }
            }
            // Same as the Discuss panel beside it: a deleted key or a revoked role is a boundary,
            // not a fault, and `AsyncFailureState` is what tells the two apart.
            is Async.Failure -> AsyncFailureState(
                failure = history,
                subject = t("editor.editor_activity_content.this_history", "this history"),
                onRetry = {
                    state.panels.history.remove(row.key.id)
                    state.panels.ensureActivityLoaded(row.key.id)
                },
            )
            is Async.Success -> if (history.value.entries.isEmpty()) {
                EditorUtilityPanelEmpty(
                    name = "history",
                    title = t("editor.editor_activity_content.no_history_yet", "No history yet"),
                    description = t("editor.editor_activity_content.saved_translations_and_approvals_will_appear", "Saved translations and approvals will appear here."),
                )
            } else {
                val entries = history.value.entries
                entries.forEachIndexed { index, entry ->
                    HistoryCard(entry, entries.getOrNull(index + 1), currentUserId)
                }
                // A page is a cap, and a cap the reader cannot see is indistinguishable from the
                // end of the record. The control appears only when there genuinely is more.
                if (history.value.nextCursor != null) {
                    // `loading`, not a swapped label: it is the component's own spinner and its
                    // `aria-busy`, and it is the shape every other append-a-page control uses —
                    // `Keys.kt`'s "Load more" is the same two lines. Swapping the label left the
                    // wait in a string no catalogue held, so a translated interface still read
                    // "Loading older changes…" in English, and the button changed width mid-request.
                    //
                    // `enabled` still goes false with it, as it does at every other such call site:
                    // that is what takes the button out of the tab order, and `loading` alone would
                    // be the way to keep it in.
                    val loadingMore = state.panels.historyLoadingMore[row.key.id] == true
                    LButton(
                        label = t("editor.editor_activity_content.show_older_changes", "Show older changes"),
                        onClick = { state.panels.loadOlderHistory(row.key.id) },
                        variant = LButtonVariant.SECONDARY,
                        enabled = !loadingMore,
                        loading = loadingMore,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}

@Composable
private fun HistoryCard(
    entry: HistoryEntryDto,
    previous: HistoryEntryDto?,
    currentUserId: String?,
) {
    val value = historyDisplay(entry.value, entry.pluralForms)
    val oldValue = previous?.let { historyDisplay(it.value, it.pluralForms) }.orEmpty()
    LCard(modifier = Modifier.width(100.percent)) {
        Column(Modifier.gap(Space.SM)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                LBadge(
                    entry.state.name.lowercase().replaceFirstChar { it.uppercase() },
                    variant = LBadgeVariant.NEUTRAL,
                )
                // Origin, where it is not a person typing. The column has been populated since V2 —
                // the memory prefill writes MEMORY so a bulk fill stays filterable — and the per-key
                // query simply never selected it, so every row read as somebody's decision.
                originBadge(entry.origin)?.let { (label, variant) -> LBadge(label, variant = variant) }
                Spacer()
                EditorAssistMetaText(compactTimestamp(entry.createdAt))
            }
            Div(Modifier.fontSize(TypeScale.BodySmall).lineHeight(1.5).toAttrs()) {
                val forms = if (previous == null || entry.pluralForms == null) {
                    emptyList()
                } else {
                    pluralDiff(previous.pluralForms, entry.pluralForms)
                }
                when {
                    previous == null -> Text(value)
                    // A plural row is several translations, and flattening them into one sentence
                    // before diffing hid which form actually changed — the only thing this panel is
                    // opened to find out.
                    entry.pluralForms != null -> if (forms.isEmpty()) Text(value) else PluralFormDiff(forms)
                    else -> LDiffText(oldValue, value)
                }
            }
            EditorAssistMetaText(authorLabel(entry.authorId, currentUserId, entry.authorLabel))
        }
    }
}

/**
 * The forms that moved, each labelled, and nothing else.
 *
 * A save that only changed `many` draws one block. A save that changed no form at all — an approval
 * — produces no blocks and the card falls back to the value, because a version card with no value
 * on it reads as a rendering failure rather than as "nothing was rewritten".
 */
@Composable
private fun PluralFormDiff(forms: List<PluralCategoryDiff>) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        forms.forEach { form ->
            Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
                Span(
                    Modifier
                        .color(palette.textMuted)
                        .fontSize(TypeScale.Micro)
                        .fontWeight(700)
                        .toAttrs(),
                ) {
                    Text(form.category.displayName())
                }
                Div(Modifier.fillMaxWidth().toAttrs()) { LDiffParts(form.parts) }
            }
        }
    }
}

/**
 * What produced a version, when it was not a person typing.
 *
 * `HUMAN` gets no badge: it is the overwhelming majority and the default reading, and badging it
 * would make the exceptions harder to see rather than easier — the same reason the queue leaves an
 * untranslated row unmarked.
 */
private fun originBadge(origin: TranslationOrigin): Pair<String, LBadgeVariant>? = when (origin) {
    TranslationOrigin.HUMAN -> null
    TranslationOrigin.MACHINE -> "Machine" to LBadgeVariant.INFO
    TranslationOrigin.MEMORY -> "Memory" to LBadgeVariant.NEUTRAL
    TranslationOrigin.IMPORT -> "Imported" to LBadgeVariant.NEUTRAL
}
